import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, filter, map, NEVER, Observable, Subscription } from 'rxjs';

import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { IconPosition } from '@fcom/ui-components';
import { AppState } from '@fcom/core';
import { unsubscribe } from '@fcom/core/utils/index';
import { CmsArticle, CmsImageData, CmsTarget } from '@fcom/core-api';
import { AspectRatioString } from '@fcom/common/interfaces/images.interface';
import { WindowRef } from '@fcom/core/providers';
import { ElementActions, ElementTypes, GaContext } from '@fcom/common/interfaces';
import { GtmService } from '@fcom/common/gtm';
import { LoginActions } from '@fcom/core/actions';
import { LoginStep } from '@fcom/core-api/login';
import { Amount } from '@fcom/dapi';
import { MediaQueryService } from '@fcom/common/services';

import { ButtonMode, ButtonTheme } from '../buttons';
import { PromoTheme } from './enum';
import { TagTheme } from '../tag';
import { ButtonState } from '../../../common-booking/modules/ancillaries/interfaces';

export interface PromoCardCtaData extends CmsTarget {
  icon?: { name: SvgLibraryIcon; position: IconPosition };
  status?: string;
  theme?: ButtonTheme;
}

@Component({
  selector: 'fcom-promo-card',
  templateUrl: './promo-card.component.html',
  styleUrls: ['./promo-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PromoCardComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private cdRef = inject(ChangeDetectorRef);
  private windowRef = inject(WindowRef);
  private gtmService = inject(GtmService);
  private store$ = inject<Store<AppState>>(Store);
  private mediaQueryService = inject(MediaQueryService);

  readonly ButtonTheme = ButtonTheme;
  readonly IconLibrary = IconLibrary;
  readonly PromoTheme = PromoTheme;
  readonly ButtonMode = ButtonMode;
  readonly TagTheme = TagTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly ButtonState = ButtonState;

  @Input()
  theme: PromoTheme = PromoTheme.WHITE;

  @Input()
  data$: Observable<CmsArticle> = NEVER;

  @Input()
  imageData$: Observable<CmsImageData> = NEVER;

  @Input()
  ctaData$: Observable<PromoCardCtaData> = NEVER;

  @Input()
  @HostBinding('class.special-grid-size-for-laptop')
  specialGridSizeForLaptop = false;

  @Input()
  aspectRatio: AspectRatioString = '21x9';

  @Input()
  isTextOnTop = false;

  @Input()
  fullWidth = false;

  @Input()
  analyticsEventName: string;

  @Input()
  isProductPromo = false;

  @Input()
  pricePrefix?: string;

  @Input()
  price?: Amount;

  @Input()
  originalPrice?: Amount;

  @Input()
  aviosPrice: string;

  @Output()
  buttonCta: EventEmitter<void> = new EventEmitter<void>();

  @HostBinding('class.double') isDouble = false;

  @HostBinding('class.full-width') isFullWidth = false;

  ctaTrigger$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  isMobile$: Observable<boolean>;

  private subscriptions: Subscription = new Subscription();

  ngOnInit(): void {
    this.subscriptions.add(
      combineLatest([this.data$, this.imageData$]).subscribe(() => {
        this.isDouble = true;
        this.isFullWidth = this.fullWidth;
        this.cdRef.markForCheck();
      })
    );

    if (this.isProductPromo) {
      this.subscriptions.add(this.ctaTrigger$.pipe(filter(Boolean)).subscribe(() => this.buttonCta.emit()));
    } else {
      this.subscriptions.add(
        combineLatest([this.ctaData$, this.ctaTrigger$.pipe(filter(Boolean))])
          .pipe(map(([cta, _]) => cta))
          .subscribe((cta) => this.navigateToUrl(cta))
      );
    }

    this.isMobile$ = this.mediaQueryService.isBreakpoint$('mobile');
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  navigateToUrl(cta: CmsTarget): void {
    if (this.analyticsEventName) {
      this.gtmService.trackElement(
        this.analyticsEventName,
        GaContext.NO_CONTEXT,
        ElementTypes.BUTTON,
        cta.url,
        ElementActions.CLICK
      );
    }

    if (cta?.url?.includes('auth.finnair.com')) {
      this.openLoginDialog();
    } else if (cta?.url && !cta?.openInNewTab) {
      this.router.navigateByUrl(cta?.url);
    } else if (cta?.url && cta?.openInNewTab) {
      this.windowRef.nativeWindow.open(cta?.url, '_blank');
    }
  }

  openLoginDialog(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    this.store$.dispatch(LoginActions.setLoginPhaseStep({ step: LoginStep.CREDENTIALS }));
  }
}
